import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TruncateMarkup, { TruncateProps } from "react-truncate-markup";
import { Subject } from "rxjs";
import { distinctUntilChanged, skip } from "rxjs/operators";

export interface IShowMoreProps {}

export function ShowMore({
  children,
  ...props
}: IShowMoreProps & TruncateProps) {
  const [truncated, setTruncated] = useState<boolean>(false);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const { t } = useTranslation("fields");

  const container = useRef<HTMLDivElement>(null);

  const sizeChange = useMemo(() => new Subject<number>(), []);

  useEffect(() => {
    const sub = sizeChange
      .pipe(distinctUntilChanged(), skip(1))
      .subscribe((x) => {
        setShowOptions(true);
      });

    return () => sub.unsubscribe();
  }, [sizeChange]);

  useEffect(() => {
    if (container.current) {
      sizeChange.next(container.current.clientHeight);
    }
  }, [container.current]);

  useEffect(() => {
    if (truncated) {
      setTimeout(() => {
        if (container.current) {
          sizeChange.next(container.current.clientHeight);
        }
      }, 0);
    }
  }, [truncated, container.current]);

  useEffect(() => {
    setTruncated(true);
  }, []);

  return (
    <div ref={container}>
      {truncated ? (
        <TruncateMarkup {...props}>
          <div>{children}</div>
        </TruncateMarkup>
      ) : (
        <div>{children}</div>
      )}
      {showOptions ? (
        truncated ? (
          <a onClick={() => setTruncated(false)}>{t("show-more")}</a>
        ) : (
          <a onClick={() => setTruncated(true)}>{t("show-less")}</a>
        )
      ) : null}
    </div>
  );
}
